import { useState } from 'react'
import { Button, Form, InputNumber, Select, Tag, Popconfirm, App } from 'antd'
import { PlusOutlined, CalculatorOutlined, CheckOutlined, EyeOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { payrollPeriodsApi } from '@/features/hr/api'
import { plantsApi } from '@/features/masters/plants/api'
import type { PayrollPeriod } from '@/shared/types/hr'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const STATUS_COLORS: Record<string, string> = {
  OPEN: 'blue', PROCESSING: 'orange', COMPUTED: 'purple', APPROVED: 'green', PAID: 'cyan',
}

export default function PayrollPeriodPage() {
  const { message } = App.useApp()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [plantFilter, setPlantFilter] = useState<string | undefined>()
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear())

  const canCreate = usePermission('payroll.create')
  const canApprove = usePermission('payroll.approve')

  const { data, isLoading } = useQuery({
    queryKey: ['payroll-periods', plantFilter, yearFilter],
    queryFn: () => payrollPeriodsApi.list({ plant_id: plantFilter, year: yearFilter }),
  })

  const { data: plantsData } = useQuery({
    queryKey: ['plants-all'],
    queryFn: () => plantsApi.list({ page: 1, page_size: 500 }),
  })

  const plantOptions = plantsData?.items.map((p) => ({ label: p.name, value: p.id })) ?? []
  const yearOptions = Array.from({ length: 5 }, (_, i) => {
    const y = new Date().getFullYear() - 2 + i
    return { label: String(y), value: y }
  })
  const monthOptions = MONTHS.map((m, i) => ({ label: m, value: i + 1 }))

  const openCreate = () => {
    form.resetFields()
    form.setFieldsValue({ year: yearFilter, month: new Date().getMonth() + 1 })
    setDrawerOpen(true)
  }

  const createMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => payrollPeriodsApi.create(values),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-periods'] })
      message.success('Payroll period created')
      setDrawerOpen(false)
    },
    onError: () => message.error('Create failed'),
  })

  const computeMutation = useMutation({
    mutationFn: (id: string) => payrollPeriodsApi.compute(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-periods'] })
      message.success('Payroll computed successfully')
    },
    onError: () => message.error('Compute failed'),
  })

  const approveMutation = useMutation({
    mutationFn: (id: string) => payrollPeriodsApi.approve(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-periods'] })
      message.success('Payroll approved')
    },
    onError: () => message.error('Approve failed'),
  })

  const onSubmit = () => form.validateFields().then((v) => createMutation.mutate(v))

  const columns = [
    { title: 'Plant', dataIndex: 'plant_name', key: 'plant_name' },
    {
      title: 'Period', key: 'period',
      render: (_: unknown, r: PayrollPeriod) => `${MONTHS[r.month - 1]} ${r.year}`,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 120,
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Computed At', dataIndex: 'computed_at', key: 'computed_at', width: 170,
      render: (v?: string) => v ? new Date(v).toLocaleString() : '—',
    },
    {
      title: 'Approved At', dataIndex: 'approved_at', key: 'approved_at', width: 170,
      render: (v?: string) => v ? new Date(v).toLocaleString() : '—',
    },
    {
      title: 'Actions', key: 'actions', width: 280,
      render: (_: unknown, record: PayrollPeriod) => (
        <div style={{ display: 'flex', gap: 4 }}>
          {(record.status === 'OPEN' || record.status === 'COMPUTED') && canCreate && (
            <Popconfirm title="Compute payroll for all employees?" onConfirm={() => computeMutation.mutate(record.id)}>
              <Button size="small" icon={<CalculatorOutlined />} loading={computeMutation.isPending}>Compute</Button>
            </Popconfirm>
          )}
          {record.status === 'COMPUTED' && canApprove && (
            <Popconfirm title="Approve this payroll?" onConfirm={() => approveMutation.mutate(record.id)}>
              <Button size="small" type="primary" icon={<CheckOutlined />}>Approve</Button>
            </Popconfirm>
          )}
          {record.status !== 'OPEN' && (
            <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/payroll/payslips?period_id=${record.id}`)}>Payslips</Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Payroll Periods"
        subtitle="Manage monthly payroll cycles"
        extra={canCreate && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Period</Button>}
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Select allowClear placeholder="Filter by plant" options={plantOptions} onChange={(v) => setPlantFilter(v)} style={{ width: 200 }} />
        <Select options={yearOptions} value={yearFilter} onChange={(v) => setYearFilter(v)} style={{ width: 100 }} />
      </div>

      <DataTable rowKey="id" columns={columns} dataSource={data} loading={isLoading} />

      <FormDrawer title="Create Payroll Period" open={drawerOpen} onClose={() => setDrawerOpen(false)} onSubmit={onSubmit} loading={createMutation.isPending} form={form}>
        <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}><Select options={plantOptions} placeholder="Select plant" /></Form.Item>
        <Form.Item name="year" label="Year" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={2020} max={2040} /></Form.Item>
        <Form.Item name="month" label="Month" rules={[{ required: true }]}><Select options={monthOptions} placeholder="Select month" /></Form.Item>
      </FormDrawer>
    </div>
  )
}
